/**
 * Componentes UI compartidos por todas las páginas de Análisis.
 *
 * Centralizamos los `NumberField`, `SelectField`, `Section`, `Row`, `Card`,
 * `RowKV` para evitar duplicar el mismo código en 5 páginas.
 */

import type { ReactNode } from 'react';

export function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div className="mb-5">
      <div
        className="mb-2 text-[10px] uppercase tracking-[0.18em]"
        style={{ color: 'var(--text-muted)' }}
      >
        {title}
      </div>
      <div className="space-y-2">{children}</div>
    </div>
  );
}

export function Row({ children, cols = 2 }: { children: ReactNode; cols?: 1 | 2 | 3 }) {
  const grid = cols === 1 ? 'grid-cols-1' : cols === 2 ? 'grid-cols-2' : 'grid-cols-3';
  return <div className={`grid ${grid} gap-2`}>{children}</div>;
}

export function NumberField({
  label,
  value,
  onChange,
  step = 1,
  compact = false,
}: {
  label: string;
  value: number;
  onChange: (v: number) => void;
  step?: number;
  compact?: boolean;
}) {
  return (
    <label className="flex flex-col gap-0.5 text-[11px]" style={{ color: 'var(--text-secondary)' }}>
      <span>{label}</span>
      <input
        type="number"
        value={Number.isFinite(value) ? value : 0}
        onChange={(e) => onChange(parseFloat(e.currentTarget.value) || 0)}
        step={step}
        className={`${compact ? 'h-8' : 'h-9'} rounded border bg-[var(--surface-2)] px-2 text-[12.5px] tabular`}
        style={{ borderColor: 'var(--border-default)', color: 'var(--text-primary)' }}
      />
    </label>
  );
}

export function SelectField({
  label,
  value,
  options,
  onChange,
}: {
  label: string;
  value: string;
  options: Record<string, string>;
  onChange: (v: string) => void;
}) {
  return (
    <label className="flex flex-col gap-0.5 text-[11px]" style={{ color: 'var(--text-secondary)' }}>
      <span>{label}</span>
      <select
        value={value}
        onChange={(e) => onChange(e.currentTarget.value)}
        className="h-9 rounded border bg-[var(--surface-2)] px-2 text-[12.5px]"
        style={{ borderColor: 'var(--border-default)', color: 'var(--text-primary)' }}
      >
        {Object.entries(options).map(([k, label]) => (
          <option key={k} value={k}>
            {label}
          </option>
        ))}
      </select>
    </label>
  );
}

export function Card({
  title,
  icon,
  children,
}: {
  title: string;
  icon?: ReactNode;
  children: ReactNode;
}) {
  return (
    <div
      className="mb-4 rounded-[var(--radius-md)] border p-4"
      style={{ background: 'var(--surface-1)', borderColor: 'var(--border-default)' }}
    >
      <div
        className="mb-3 flex items-center gap-1.5 text-[10.5px] uppercase tracking-[0.18em]"
        style={{ color: 'var(--text-muted)' }}
      >
        {icon && <span style={{ color: 'var(--accent)' }}>{icon}</span>}
        {title}
      </div>
      {children}
    </div>
  );
}

export function RowKV({
  k,
  v,
  unit,
  bold,
  accent,
}: {
  k: string;
  v: number | string;
  unit?: string;
  bold?: boolean;
  accent?: boolean;
}) {
  const display =
    typeof v === 'number'
      ? v.toLocaleString('es-PE', { maximumFractionDigits: 1 })
      : v;
  return (
    <div className="flex items-baseline justify-between gap-2">
      <span style={{ color: 'var(--text-muted)' }}>{k}</span>
      <span
        className={`tabular ${bold ? 'font-semibold' : ''}`}
        style={{ color: accent ? 'var(--accent)' : 'var(--text-primary)' }}
      >
        {display}
        {unit && (
          <span className="ml-1 text-[10px]" style={{ color: 'var(--text-muted)' }}>
            {unit}
          </span>
        )}
      </span>
    </div>
  );
}

export function Divider() {
  return <div className="my-1.5 border-t" style={{ borderColor: 'var(--border-subtle)' }} />;
}

export function ForceItem({
  label,
  value,
  unit,
}: {
  label: string;
  value: number;
  unit: string;
}) {
  return (
    <div
      className="rounded-[var(--radius-sm)] border p-2"
      style={{ background: 'var(--surface-2)', borderColor: 'var(--border-default)' }}
    >
      <div
        className="text-[10px] uppercase tracking-wider"
        style={{ color: 'var(--text-muted)' }}
      >
        {label}
      </div>
      <div
        className="mt-0.5 tabular text-[13px] font-semibold"
        style={{ color: 'var(--text-primary)' }}
      >
        {value.toLocaleString('es-PE', { maximumFractionDigits: 0 })}
      </div>
      <div className="text-[10px]" style={{ color: 'var(--text-muted)' }}>
        {unit}
      </div>
    </div>
  );
}

export function WarningsCard({ items }: { items: string[] }) {
  if (items.length === 0) return null;
  return (
    <Card title="Advertencias">
      <ul
        className="list-disc space-y-1 pl-5 text-[12px]"
        style={{ color: 'var(--text-secondary)' }}
      >
        {items.map((w, i) => (
          <li key={i}>{w}</li>
        ))}
      </ul>
    </Card>
  );
}
